import { useQuery } from '@tanstack/react-query'

import { POLL_NORMAL } from '@/api/polling'
import { useAuthenticatedClient } from '@/api/queries'
import type { components } from '@/api/schema'

export type ProjectPayments = components['schemas']['QbProjectPaymentsRead']
export type ProjectInvoice = components['schemas']['QbProjectInvoiceRead']
export type InvoiceStatus = ProjectInvoice['status']

export const projectPaymentsQueryKey = (projectUuid: string) => ['projects', 'payments', projectUuid] as const

/** Invoices and payments for a project, read from our ledger (kept current by Stripe webhooks and an hourly check). */
export function useProjectPayments(projectUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: projectPaymentsQueryKey(projectUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/quote-builder/projects/{project_uuid}/payments', {
        params: { path: { project_uuid: projectUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!projectUuid,
    refetchInterval: POLL_NORMAL,
  })
}

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Pending',
  open: 'Awaiting payment',
  paid: 'Paid',
  void: 'Void',
  uncollectible: 'Uncollectible',
}

export function invoiceOverdue(invoice: Pick<ProjectInvoice, 'status' | 'due_at'>, now = new Date()) {
  return invoice.status === 'open' && !!invoice.due_at && new Date(invoice.due_at) < now
}

export function invoiceStatusLabel(invoice: Pick<ProjectInvoice, 'status' | 'due_at'>, now = new Date()) {
  return invoiceOverdue(invoice, now) ? 'Overdue' : STATUS_LABELS[invoice.status]
}

export function invoiceStatusTone(invoice: Pick<ProjectInvoice, 'status' | 'due_at'>, now = new Date()) {
  if (invoice.status === 'paid') return 'success' as const
  if (invoiceOverdue(invoice, now) || invoice.status === 'uncollectible') return 'danger' as const
  if (invoice.status === 'open') return 'warning' as const
  return 'neutral' as const
}
